import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState, useEffect } from 'react';
import { txt } from 'libs/i18n';
import { Form } from 'antd';
import { debounce } from 'throttle-debounce';
import { parseSpeaker } from 'libs/parse-speakers';
import { fetchDuplicateSpeakers } from 'api/speaker-api-v2';
import { useSession } from 'components/Authenticated';
import AvatarField from 'components/SpeakerAvatarField';
import LanguageSelect from 'components/LanguageSelect';
import { createSpeaker, addLocalizationToSpeaker } from '../../speaker-actions';
import AvailabilitySettings from '../../AvailabilitySettings';
import SpeakerDetails from '../../SpeakerDetails';
import SpeakerForm from '../../SpeakerForm';
import Role from '../../Role';
import DuplicateSpeakerAlert from '../../DuplicateSpeakerAlert';
import ColorPicker from '../../ColorPicker';
import styles from './style.module.less';
const CreateTab = ({ form, mode, onChangeTab, setSubmitState, onClose, isCaptionMode, defaultSpeakerColor, searchText, onSubmit, languages, onEditRedirect, }) => {
    const { session, session: { teamSettings } } = useSession();
    const [duplicateSpeaker, setDuplicateSpeaker] = useState({
        state: 'idle', data: null, selectedIndex: 0,
    });
    const [mergeWithExisting, setMergeWithExisting] = useState(false);
    const firstName = Form.useWatch(['speaker', 'firstName'], form);
    const lastName = Form.useWatch(['speaker', 'lastName'], form);
    const availability = Form.useWatch('availability', form);
    const selectedLocalization = Form.useWatch('localization', form);
    const isMultipleLanguages = Array.isArray(languages) && languages.length > 1;
    const parsedSearchText = useMemo(() => (searchText !== undefined ? parseSpeaker(searchText) : undefined), [searchText]);
    const fieldWithAutofocus = useMemo(() => {
        if (isMultipleLanguages) {
            return 'localization';
        }
        if (parsedSearchText === undefined) {
            return 'firstName';
        }
        if (parsedSearchText.lastName !== '' || parsedSearchText.role !== '') {
            return 'role';
        }
        return 'lastName';
    }, [parsedSearchText, isMultipleLanguages]);
    const checkDuplicates = useMemo(() => debounce(1000, async (fn, ln) => {
        const hasFn = fn !== undefined && fn !== '';
        const hasLn = ln !== undefined && ln !== '';
        if (selectedLocalization === undefined || (!hasFn && !hasLn)) {
            setDuplicateSpeaker({
                state: 'idle', data: null, selectedIndex: 0,
            });
            return;
        }
        const result = await fetchDuplicateSpeakers(session.connection, {
            firstName: fn !== null && fn !== void 0 ? fn : null,
            lastName: ln !== null && ln !== void 0 ? ln : '',
        });
        if (result.isSuccess() && result.get().list.length > 0) {
            setDuplicateSpeaker({
                state: 'found',
                data: result.get().list,
                selectedIndex: 0,
            });
            return;
        }
        setDuplicateSpeaker({
            state: 'notFound', data: null, selectedIndex: 0,
        });
    }), [session.connection, selectedLocalization]);
    useEffect(() => {
        if (availability !== 'all') {
            setDuplicateSpeaker({
                state: 'idle', data: null, selectedIndex: 0,
            });
            return;
        }
        const hasFn = firstName !== undefined && firstName !== '';
        const hasLn = lastName !== undefined && lastName !== '';
        if (hasFn || hasLn) {
            setDuplicateSpeaker({
                state: 'loading', data: null, selectedIndex: 0,
            });
            void checkDuplicates(firstName, lastName);
        }
    }, [firstName, lastName, availability, checkDuplicates]);
    useEffect(() => () => checkDuplicates.cancel(), [checkDuplicates]);
    useEffect(() => {
        if (parsedSearchText === undefined) {
            return;
        }
        form.setFieldsValue({
            speaker: {
                firstName: parsedSearchText.firstName,
                lastName: parsedSearchText.lastName,
            },
            role: parsedSearchText.role !== '' ? { label: parsedSearchText.role, isDefault: true } : null,
        });
    }, [parsedSearchText, form]);
    const handleEditTab = () => {
        if (duplicateSpeaker.state !== 'found' || duplicateSpeaker.data === null) {
            return;
        }
        const selectedItem = duplicateSpeaker.data[duplicateSpeaker.selectedIndex];
        if (onEditRedirect !== undefined) {
            onEditRedirect(selectedItem.similarSpeaker.neutralData.uniqueId, selectedLocalization);
            return;
        }
        onChangeTab('edit', {
            id: selectedItem.similarSpeaker.neutralData.uniqueId,
            language: isMultipleLanguages ? selectedLocalization : undefined,
        });
    };
    const handleSubmit = async (formValues) => {
        var _a, _b, _c;
        setSubmitState('loading');
        const { speakerIdActive, role, speaker, imageBase64, localization, } = formValues;
        const isDefaultColor = speaker.captionColor === defaultSpeakerColor;
        let newSpeaker = null;
        const activeDuplicateData = duplicateSpeaker.state === 'found' && duplicateSpeaker.data !== null
            ? duplicateSpeaker.data[duplicateSpeaker.selectedIndex].similarSpeaker
            : null;
        if (mergeWithExisting && activeDuplicateData !== null) {
            newSpeaker = await addLocalizationToSpeaker(session.connection, activeDuplicateData, localization.code, speakerIdActive !== null && speakerIdActive !== void 0 ? speakerIdActive : false, Object.assign(Object.assign({}, speaker), { isDefaultColor: speaker.captionColor === defaultSpeakerColor, role: (_a = role === null || role === void 0 ? void 0 : role.label) !== null && _a !== void 0 ? _a : null }));
        }
        else {
            newSpeaker = await createSpeaker(session.connection, Object.assign(Object.assign({ speakerIdActive, localization: localization.code, isDefaultColor,
                imageBase64 }, speaker), { firstName: (_b = speaker.firstName) !== null && _b !== void 0 ? _b : null, role: (_c = role === null || role === void 0 ? void 0 : role.label) !== null && _c !== void 0 ? _c : null }), availability, localization.code);
        }
        if (newSpeaker !== null) {
            setSubmitState('idle');
            onSubmit === null || onSubmit === void 0 ? void 0 : onSubmit(newSpeaker);
            onClose();
        }
    };
    const disabledLanguage = !isMultipleLanguages && mode === 'create';
    const isSelectMode = mode === 'select';
    return (_jsxs(SpeakerForm, { form: form, onSubmit: handleSubmit, submitOnEnter: true, initialValues: {
            speaker: {
                firstName: '',
                lastName: '',
                captionColor: defaultSpeakerColor,
                isDefaultColor: true,
            },
            availability: isSelectMode && !teamSettings.speakerSaveToDbDefault ? 'current' : 'all',
            speakerIdActive: teamSettings.speakerIdDefault,
            role: null,
            localization: isMultipleLanguages
                ? undefined
                : Array.isArray(languages)
                    ? languages[0]
                    : (languages !== null && languages !== void 0 ? languages : undefined),
        }, children: [_jsx(Form.Item, { name: "localization", label: txt('language'), rules: isMultipleLanguages
                    ? [{ required: true, message: txt('languageRequired') }]
                    : [], hidden: isSelectMode, tooltip: disabledLanguage ? txt('languageFilteredTooltip') : undefined, children: _jsx(LanguageSelect, { locales: isMultipleLanguages ? languages : [], autoFocus: fieldWithAutofocus === 'localization', disabled: disabledLanguage }) }), _jsxs("div", { className: styles.headerSection, children: [_jsx(SpeakerDetails, { autofocusField: fieldWithAutofocus }), _jsx(AvatarField, { name: "imageBase64", size: 70, editable: availability === 'all' })] }), _jsx(Role, { autoFocus: fieldWithAutofocus === 'role' }), _jsx(AvailabilitySettings, { rules: [{ required: true, message: txt('availabilityRequired') }], hidden: { availability: !isSelectMode }, applyDefaultSpeakerId: true }), isCaptionMode && defaultSpeakerColor !== undefined && (_jsx(ColorPicker, { defaultSpeakerColor: defaultSpeakerColor })), duplicateSpeaker.state !== 'idle' && (_jsx(DuplicateSpeakerAlert, { onEdit: handleEditTab, duplicateSpeaker: duplicateSpeaker, onIndexChange: (index) => setDuplicateSpeaker((prev) => (Object.assign(Object.assign({}, prev), { selectedIndex: index }))), language: selectedLocalization, mergeChecked: mergeWithExisting, onMergeChange: setMergeWithExisting }))] }));
};
export default CreateTab;
